import { useCallback, useEffect, useRef, useState } from 'react';
import './App.scss';
import clearSky from './assets/images/clear-sky.webp';
import cloud01 from './assets/images/cloud-texture-01.webp';
import cloud03 from './assets/images/cloud-texture-03.webp';
import cloud08 from './assets/images/cloud-texture-08.webp';
import cloud20 from './assets/images/cloud-texture-20.webp';
import cloud50 from './assets/images/cloud-texture-50.webp';
import cloud81 from './assets/images/cloud-texture-81.webp';
import bird1 from './assets/images/bird-silhouette-1.png';
import bird2 from './assets/images/bird-silhouette-2.png';
import bird3 from './assets/images/bird-silhouette-3.png';
import bird4 from './assets/images/bird-silhouette-4.png';
import bird5 from './assets/images/bird-silhouette-5.png';
import bird6 from './assets/images/bird-silhouette-6.png';
import bird7 from './assets/images/bird-silhouette-7.png';
import portrait from './assets/images/kristian-portrait.png';
import graduationCap from './assets/images/graduation-cap.png';
import graduationGown from './assets/images/graduation-gown.png';
import tearDrop from './assets/images/traene.png';
import LanguageSelect from './components/LanguageSelect';
import { CONTACT_EMAIL, LanguageContext, useT } from './i18n';

// The portrait flip (see `character-swap` in App.scss) lasts 1100ms and is
// edge-on after exactly half of that. Outfit and copy are swapped at that
// moment, so the viewer never sees the change happen.
const SWAP_MS = 550;

// Duration of the copy fade-out before the text is swapped in work mode.
const WORK_SWAP_MS = 480;

export function Portfolio({ waiting = false }) {
  const t = useT();
  const [aboutOpen, setAboutOpen] = useState(false);
  const [copyAbout, setCopyAbout] = useState(false);
  const [portraitSpin, setPortraitSpin] = useState(0);
  const [isPortraitSpinning, setIsPortraitSpinning] = useState(false);
  const [hasGraduationOutfit, setHasGraduationOutfit] = useState(false);
  const [workOpen, setWorkOpen] = useState(false);
  const [copyWork, setCopyWork] = useState(false);
  const [workPhase, setWorkPhase] = useState('');
  const sceneRef = useRef(null);
  const aboutOpenRef = useRef(false);
  const outfitTimerRef = useRef(null);
  const workTimersRef = useRef([]);
  const pendingWorkRef = useRef(false);

  const clearOutfitTimer = () => {
    if (outfitTimerRef.current !== null) {
      window.clearTimeout(outfitTimerRef.current);
      outfitTimerRef.current = null;
    }
  };

  const settleScene = useCallback(() => {
    if (outfitTimerRef.current !== null) {
      window.clearTimeout(outfitTimerRef.current);
      outfitTimerRef.current = null;
    }
    setIsPortraitSpinning(false);
    setHasGraduationOutfit(aboutOpenRef.current);
    setCopyAbout(aboutOpenRef.current);
  }, []);

  const resetPointerPosition = (scene) => {
    if (!scene) return;
    scene.style.setProperty('--pointer-x', '0px');
    scene.style.setProperty('--pointer-y', '0px');
    scene.style.setProperty('--portrait-tilt-x', '0deg');
    scene.style.setProperty('--portrait-tilt-y', '0deg');
  };

  useEffect(() => {
    const handleWindowBlur = () => {
      resetPointerPosition(sceneRef.current);
      settleScene();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') handleWindowBlur();
    };

    window.addEventListener('blur', handleWindowBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearOutfitTimer();
      window.removeEventListener('blur', handleWindowBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [settleScene]);

  const clearWorkTimers = useCallback(() => {
    workTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    workTimersRef.current = [];
  }, []);

  // Fades the copy out, swaps it, then fades the new copy in.
  const swapWorkCopy = useCallback((nextCopyWork) => {
    clearWorkTimers();
    setWorkPhase('leaving');
    workTimersRef.current = [
      window.setTimeout(() => {
        setCopyWork(nextCopyWork);
        setWorkPhase('entering');
      }, WORK_SWAP_MS),
      window.setTimeout(() => setWorkPhase(''), WORK_SWAP_MS + 520),
    ];
  }, [clearWorkTimers]);

  const startWork = useCallback(() => {
    setWorkOpen(true);
    swapWorkCopy(true);
  }, [swapWorkCopy]);

  const stopWork = useCallback(() => {
    setWorkOpen(false);
    swapWorkCopy(false);
  }, [swapWorkCopy]);

  useEffect(() => clearWorkTimers, [clearWorkTimers]);

  // "Werkstudent" while About is open: first flip back, then cry.
  useEffect(() => {
    if (!isPortraitSpinning && !aboutOpen && pendingWorkRef.current) {
      pendingWorkRef.current = false;
      // The flip back already swapped in the Werkstudent copy; only the tears are missing.
      if (copyWork) setWorkOpen(true);
      else startWork();
    }
  }, [isPortraitSpinning, aboutOpen, copyWork, startWork]);

  const switchAbout = (thenWork) => {
    const nextAboutOpen = !aboutOpen;
    clearOutfitTimer();
    pendingWorkRef.current = thenWork;
    aboutOpenRef.current = nextAboutOpen;
    setAboutOpen(nextAboutOpen);
    setPortraitSpin((spin) => spin + 1);
    setIsPortraitSpinning(true);
    if (nextAboutOpen && (workOpen || copyWork)) {
      clearWorkTimers();
      setWorkOpen(false);
      setWorkPhase('');
    }
    outfitTimerRef.current = window.setTimeout(() => {
      setHasGraduationOutfit(nextAboutOpen);
      setCopyAbout(nextAboutOpen);
      // Going from About straight to Werkstudent skips the start page copy.
      setCopyWork(!nextAboutOpen && thenWork);
      outfitTimerRef.current = null;
    }, SWAP_MS);
  };


  const toggleAbout = () => switchAbout(false);

  const toggleWork = () => {
    if (aboutOpen) {
      switchAbout(true);
    } else if (workOpen) {
      stopWork();
    } else {
      startWork();
    }
  };

  const handlePointerMove = (event) => {
    if (event.pointerType !== 'mouse') return;

    const bounds = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - bounds.left) / bounds.width - 0.5;
    const y = (event.clientY - bounds.top) / bounds.height - 0.5;
    const scene = event.currentTarget;

    scene.style.setProperty('--pointer-x', `${x * 16}px`);
    scene.style.setProperty('--pointer-y', `${y * 12}px`);
    scene.style.setProperty('--portrait-tilt-x', `${y * -4}deg`);
    scene.style.setProperty('--portrait-tilt-y', `${x * 5}deg`);
  };

  const resetPointer = (event) => {
    resetPointerPosition(event.currentTarget);
  };

  return (
    <main
      ref={sceneRef}
      className={`portfolio sky-scene${aboutOpen ? ' about-open' : ''}${waiting ? ' is-waiting' : ''}`}
      onPointerMove={handlePointerMove}
      onPointerLeave={resetPointer}
    >
      <img className="sky-image" src={clearSky} alt="" aria-hidden="true" />
      <div className="sky-tint" aria-hidden="true" />

      <div className="bird-layer" aria-hidden="true">
        <img className="bird bird-one bird-far" src={bird1} alt="" />
        <img className="bird bird-two bird-mid" src={bird2} alt="" />
        <img className="bird bird-three bird-near" src={bird3} alt="" />
        <img className="bird bird-four bird-far" src={bird4} alt="" />
        <img className="bird bird-five bird-near" src={bird5} alt="" />
        <img className="bird bird-six bird-far" src={bird6} alt="" />
        <img className="bird bird-seven bird-mid" src={bird7} alt="" />
      </div>

      <div className="cloud-layer cloud-layer-back" aria-hidden="true">
        <div className="cloud-hitbox cloud-back-left"><img className="cloud" src={cloud01} alt="" /></div>
        <div className="cloud-hitbox cloud-back-right"><img className="cloud" src={cloud08} alt="" /></div>
        <div className="cloud-hitbox cloud-back-low"><img className="cloud" src={cloud50} alt="" /></div>
      </div>
      <div className="cloud-layer cloud-layer-front" aria-hidden="true">
        <div className="cloud-hitbox cloud-front-left"><img className="cloud" src={cloud20} alt="" /></div>
        <div className="cloud-hitbox cloud-front-right"><img className="cloud" src={cloud81} alt="" /></div>
        <div className="cloud-hitbox cloud-front-low"><img className="cloud" src={cloud03} alt="" /></div>
      </div>

      <section
        className="hero"
        id="start"
        aria-labelledby={copyAbout ? 'about-title' : copyWork ? 'work-title' : 'hero-title'}
      >
        <div
          className={[
            'hero-copy',
            portraitSpin === 0 ? 'is-intro' : '',
            isPortraitSpinning ? (copyAbout !== aboutOpen ? 'is-leaving' : 'is-entering') : '',
            workPhase ? `is-${workPhase}` : '',
          ].filter(Boolean).join(' ')}
        >
          {copyAbout ? (
            <article className="about-card" aria-labelledby="about-title">
              <p className="about-card-label">{t.eyebrow}</p>
              <h1 id="about-title">{t.aboutTitle}</h1>
              {t.aboutText.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            </article>
          ) : copyWork ? (
            <article className="about-card work-card" aria-labelledby="work-title">
              <p className="about-card-label">{t.workLabel}</p>
              <h1 id="work-title">{t.workTitle[0]}<span>{t.workTitle[1]}</span></h1>
              <p>{t.workText}</p>
            </article>
          ) : (
            <>
              <p className="eyebrow">{t.eyebrow}</p>
              <h1 id="hero-title">
                Kristian
                <span className="surname">Obolewicz<span className="title-period">.</span></span>
              </h1>
              <p className="hero-description">
                {t.heroDescription}
                <span>{t.heroDescriptionTail}</span>
              </p>
              <div className="study-label">
                <span className="study-line" />
                <span>{t.studyLabel[0]}<br />{t.studyLabel[1]}</span>
              </div>
            </>
          )}
        </div>

        <div className={`portrait-stage${workOpen ? ' is-crying' : ''}`} aria-label={t.portraitStage}>
          {workOpen && (
            <div className="team-note">
              <span>{t.teamNote}</span>
              <svg viewBox="0 0 160 90" aria-hidden="true">
                <path pathLength="1" d="M150 4 C 132 40, 70 62, 12 80" />
                <path pathLength="1" className="team-note-head" d="M26 70 L 12 80 L 28 84" />
              </svg>
            </div>
          )}
          <div className="character-frame">
            <div
              key={portraitSpin}
              className={[
                'portrait-turn',
                aboutOpen ? 'is-about' : '',
                isPortraitSpinning ? 'is-spinning' : '',
                isPortraitSpinning && !aboutOpen ? 'is-returning' : '',
              ].filter(Boolean).join(' ')}
              onAnimationEnd={(event) => {
                if (event.target !== event.currentTarget) return;
                settleScene();
              }}
            >
              <img
                className="character-portrait"
                src={portrait}
                alt={t.portraitAlt}
              />
              {(aboutOpen || hasGraduationOutfit || isPortraitSpinning) && (
                <div className={`graduation-outfit${hasGraduationOutfit ? ' is-visible' : ''}`} aria-hidden="true">
                  <img className="graduation-gown" src={graduationGown} alt="" />
                  <img className="graduation-cap" src={graduationCap} alt="" />
                </div>
              )}
              {workOpen && (
                <div className="tear-stream" aria-hidden="true">
                  <img className="tear-drop tear-left" src={tearDrop} alt="" />
                  <img className="tear-drop tear-right" src={tearDrop} alt="" />
                </div>
              )}
            </div>
          </div>
          <a className="mail-card" href={`mailto:${CONTACT_EMAIL}`}>
            <span className="mail-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <rect x="3" y="5.5" width="18" height="13" rx="1.5" />
                <path d="M3.5 6.5 12 13l8.5-6.5" />
              </svg>
            </span>
            <span className="mail-text">
              <strong>{t.mailTitle}</strong>
              <small>{t.mailHint}</small>
            </span>
            <span className="mail-arrow" aria-hidden="true">→</span>
          </a>
        </div>

        <div className="scene-footer">
          <button
            className="footer-button"
            type="button"
            aria-expanded={aboutOpen}
            onClick={toggleAbout}
          >
            {aboutOpen ? t.navBack : t.navAbout}
          </button>
          <button
            className="footer-button"
            type="button"
            aria-expanded={workOpen}
            onClick={toggleWork}
          >
            {workOpen ? t.navBack : t.navWork}
          </button>

        </div>
      </section>
    </main>
  );
}

function App() {
  const [lang, setLang] = useState(null);
  const [gateOpen, setGateOpen] = useState(true);

  useEffect(() => {
    if (lang) document.documentElement.lang = lang;
  }, [lang]);

  // The portfolio sits zoomed in behind the frosted language gate with its
  // intro paused; picking a language zooms it out while the glass clears.
  return (
    <LanguageContext.Provider value={lang ?? 'de'}>
      <div className={`app-stage${lang ? ' is-revealed' : ''}`}>
        <Portfolio waiting={!lang} />
      </div>
      {gateOpen && (
        <LanguageSelect onSelect={setLang} onDone={() => setGateOpen(false)} />
      )}
    </LanguageContext.Provider>
  );
}

export default App;
